import { IPartialChildNodeWithIndex, NodeName } from './interfaces/patternsHtmlTags.interface';
import { IParsedHtml, ParsedType } from './interfaces/parsedHtml';
import { IDelimiter, ITopLevelGroup, CustomDelimiter } from './interfaces/patternsConfig.interface';
import { findLastIndex } from '../../../../utils/findLastIndex';
import { getAllIndexes } from '../../../../utils/getAllIndexes';

export class PatternsReader {
  constructor(private htmlNodes: IPartialChildNodeWithIndex[]) {}

  setHtmlNodes(htmlNodes: IPartialChildNodeWithIndex[]): void {
    this.htmlNodes = htmlNodes;
  }

  get (index: number): IPartialChildNodeWithIndex {
    return this.htmlNodes[index];
  }

  readSequencial(
    fromIndex: number,
    sequencialNode: NodeName,
  ): IPartialChildNodeWithIndex [] {
    const nodes = [];
    let currentIndex = fromIndex;
    let IsInSequence = true;

    while (IsInSequence && currentIndex < this.htmlNodes.length) {
      const currentNode = this.htmlNodes[currentIndex];

      if (currentNode.nodeName === sequencialNode) {
        nodes.push(currentNode);
      } else {
        IsInSequence = false;
      }

      currentIndex += 1;
    }

    return nodes;
  }

  readSequencialWithFilter(
    fromIndex: number,
    isSequencial: (node: IPartialChildNodeWithIndex) => boolean,
  ): IPartialChildNodeWithIndex [] {
    const nodes = [];
    let currentIndex = fromIndex;
    let IsInSequence = true;

    while (IsInSequence && currentIndex < this.htmlNodes.length) {
      const currentNode = this.htmlNodes[currentIndex];

      if (isSequencial(currentNode)) {
        nodes.push(currentNode);
      } else {
        IsInSequence = false;
      }

      currentIndex += 1;
    }

    return nodes;
  }

  static getGlobalIndex(nodes: IParsedHtml[], index: number): number {
    return nodes[index]?.line?.[0] || -1;
  }

  static getFinalGlobalIndex(nodes: IParsedHtml[], index: number): number {
    return nodes[index]?.line?.[1] || -1;
  }

  static getNodesFromDelimiters(nodes: IParsedHtml[], topLevelGroup: ITopLevelGroup): {
    nodes: IParsedHtml[],
    startDelimiter?: IParsedHtml,
    endDelimiter?: IParsedHtml,
  } {
    const startDelimiterIndex = this.getDelimiterIndex(nodes, topLevelGroup.delimiters[0]);
    const endDelimiterIndex = this.getDelimiterIndex(nodes, topLevelGroup.delimiters[1]);

    return {
      nodes: nodes.slice(startDelimiterIndex + 1, endDelimiterIndex),
      startDelimiter: nodes[startDelimiterIndex],
      endDelimiter: nodes[endDelimiterIndex],
    };
  }

  static getDelimiterIndex(nodes: IParsedHtml[], delimiter: IDelimiter): number {
    if (this.isCustomDelimiter(delimiter)) {
      const bindings = {
        start: -1,
        end: nodes.length,
      };

      return bindings[delimiter.delimiter as CustomDelimiter];
    }

    if (delimiter.first) {
      return nodes.findIndex(node => node.type === delimiter.delimiter);
    }

    if (delimiter.last) {
      return findLastIndex(nodes, (node) => {
        return node.type === delimiter.delimiter;
      });
    }

    return -1;
  }

  static isCustomDelimiter(delimiterConf: IDelimiter): boolean {
    const customDelimiters: CustomDelimiter[] = ['start', 'end'];

    if (customDelimiters.includes(delimiterConf.delimiter as CustomDelimiter)) {
      return true;
    }

    return false;
  }

  static getNodesFromIndex(nodes: IParsedHtml[], index: number): IParsedHtml[] {
    return nodes.slice(index);
  }

  static separateNodesWith(nodes: IParsedHtml[], separator: ParsedType): IParsedHtml[][] {
    const separatedTags: IParsedHtml[][] = [];
    const indexes = getAllIndexes(nodes.map(node => node.type), separator);

    if (indexes.length === 0) return [nodes];

    let lastSlicedIndex = 0;
    indexes.forEach((index) => {
      const slicedTags = nodes.slice(lastSlicedIndex, index);
      separatedTags.push(slicedTags);
      lastSlicedIndex = index + 1;
    });

    const lastSlicedTags = nodes.slice(lastSlicedIndex);
    separatedTags.push(lastSlicedTags);

    return separatedTags;
  }

  static getSeparatorsFrom(nodes: IParsedHtml[], separator: ParsedType): IParsedHtml[] {
    const indexes = getAllIndexes(nodes.map(node => node.type), separator);
    const separators = indexes.map(index => nodes[index]);
    return separators;
  }

  static getByGlobalIndex(nodes: IParsedHtml[], globalIndex: number): IParsedHtml | undefined {
    return nodes.find(node =>
      node.line[0] >= globalIndex &&
      node.line[1] <= globalIndex);
  }
}
